import { useState } from 'react';
import {
  Accordion,
  AccordionItem,
  AccordionSummary,
  AccordionDetails,
} from '@packages/shared/src/ui/Accordion/Accordion';
import { StatusChip } from '@packages/shared/src/ui/StatusChip/StatusChip';
import { Divider } from '@packages/shared/src/ui/Divider/Divider';
import { Checkbox } from '@packages/shared/src/ui/Checkbox/Checkbox';
import { Image } from '@packages/shared/src/ui/Image/Image';
import { Dialog } from '@packages/shared/src/ui/Dialog/Dialog';
import { DialogHeader } from '@packages/modules/src/Dialog/DialogHeader/DialogHeader.client';
import { DialogContent } from '@packages/shared/src/ui/Dialog/DialogContent/DialogContent';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import type { FragmentType } from '@packages/gql/generated/shopping';
import { getFragmentData } from '@packages/gql/generated/shopping';
import { useAuthMutation } from '@packages/shared/src/hooks/useAuthMutation/useAuthMutation';
import { AplusFragmentFragmentDoc } from '@packages/gql/generated/shopping/AplusFragmentFragmentDoc';
import { ToggleServiceProductDocument } from '@packages/gql/generated/shopping/ToggleServiceProductDocument';
import { ServiceLayer } from '@packages/cms-components/src/modules/ServiceLayer/ServiceLayer';
import { useConfig } from '@packages/shared/src/hooks/useConfig/useConfig';
import { PriceValue } from '@packages/shared/src/ui/Price/PriceValue/PriceValue';
import { Price } from '@packages/shared/src/ui/Price/Price';
import { useFormatPrice } from '@packages/shared/src/hooks/useFormatPrice/useFormatPrice';
import { useTracking } from '@packages/tracking/src/hooks/useTracking/useTracking';
import { AplusServiceCountTextField } from './AplusServiceCountTextField';

/* GraphQL */ `
  fragment AplusFragment on BasketAPlus {
    clusters {
      label
      services {
        serviceType
        serviceIcon
        uuid
        name
        price {
          value
          currency
        }
        included
        layerLink
        selected
        serviceCount {
          current
          maximum
        }
      }
    }
  }
`;

type AplusProps = {
  aplusData: FragmentType<typeof AplusFragmentFragmentDoc>;
  lineItemId: string;
  quantity: number;
};

/* GraphQL */ `
  mutation ToggleServiceProduct(
    $locale: String!
    $lineItemId: String!
    $serviceUuid: String!
    $count: Int
  ) {
    toggleServiceProduct(
      locale: $locale
      lineItemId: $lineItemId
      serviceUuid: $serviceUuid
      count: $count
    ) {
      ...BasketFragment
    }
  }
`;

const messages = defineMessages({
  aplusInfoAriaLabel: {
    id: 'Aplus.info.aria-label',
    defaultMessage: 'Weitere Informationen zum Service',
  },
});

export const Aplus = ({ aplusData, lineItemId, quantity }: AplusProps) => {
  const { locale, formatMessage } = useIntl();
  const fragmentData = getFragmentData(AplusFragmentFragmentDoc, aplusData);
  const { clusters } = fragmentData || {};
  const [activeServiceDialog, setActiveServiceDialog] = useState<
    { uuid: string; name: string } | undefined
  >(undefined);
  const [state, toggleServiceProduct] = useAuthMutation(ToggleServiceProductDocument);
  const dispatchGTMEvent = useTracking();
  const {
    order: { aplusIdentifier },
  } = useConfig();
  const { formatPrice } = useFormatPrice();
  const [accordionKey, setAccordionKey] = useState('aplus');

  const onClickHandler = (serviceUuid: string, count?: number) => {
    if (!state.fetching) {
      toggleServiceProduct({ locale, lineItemId, serviceUuid, count });
    }
  };

  let timeout: NodeJS.Timeout;
  const handleCountChange = (serviceUuid: string, count: number) => {
    // use a timeout to prevent too many backend calls
    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = setTimeout(() => {
      toggleServiceProduct({ locale, lineItemId, serviceUuid, count });
    }, 1500);
  };

  const openServiceInfo = (serviceName: string, serviceUuid: string) => {
    dispatchGTMEvent({
      event: 'DisplayOverlay',
      DisplayOverlayData: {
        category: 'display-overlay',
        label: serviceName,
      },
    });
    setActiveServiceDialog({
      name: serviceName,
      uuid: aplusIdentifier === 'uuid' ? serviceUuid : serviceUuid.split('-')[1],
    });
  };

  return (
    <Accordion type="single" collapsible value={accordionKey} onValueChange={setAccordionKey}>
      <AccordionItem value="aplus" noStyle>
        <AccordionSummary className="text-size-body-sm">
          {accordionKey !== 'aplus' && (
            <StatusChip color="success" className="mr-2">
              <FormattedMessage key="Aplus.hint-text" id="Aplus.hint" defaultMessage="TIPP" />
            </StatusChip>
          )}
          <FormattedMessage
            key="Aplus.order-text"
            id="Aplus.order"
            defaultMessage="Services jetzt dazu bestellen"
          />
        </AccordionSummary>
        <AccordionDetails>
          {clusters.map((cluster, key) => (
            // eslint-disable-next-line react/no-array-index-key
            <div key={`${lineItemId}-cluster-${key}`}>
              <Divider />
              <p className="text-size-body-xs py-2 font-semibold">{cluster?.label}</p>
              {cluster?.services.map((service) => (
                <div key={service.uuid} className="flex flex-nowrap items-start gap-2">
                  {/* Checkbox */}
                  <div className="flex-shrink-0">
                    {service.included ? (
                      <div
                        className="icon-emp-checkmark text-color-success text-size-h3 mr-[0.625rem]"
                        data-testid="aplus-checkmark"
                      />
                    ) : (
                      <Checkbox
                        data-testid="aplus-checkbox"
                        checked={!!service.selected}
                        onChange={() => onClickHandler(service.uuid)}
                      />
                    )}
                  </div>
                  {/* Quantity (if > 0) */}
                  {quantity &&
                    quantity > 1 &&
                    service.selected &&
                    service.serviceCount === null && (
                      <StatusChip data-testid="aplus-quantity">{quantity}</StatusChip>
                    )}
                  {/* Service Name */}
                  <div className="flex-shrink flex-grow">
                    <div className="flex items-center gap-2">
                      <Image
                        width="28"
                        height="28"
                        unoptimized
                        src={service.serviceIcon}
                        alt={service.name || ''}
                      />
                      <div>
                        <p className="text-size-body-sm">{service.name}</p>
                        {service.selected && service.serviceCount?.current && (
                          <AplusServiceCountTextField
                            count={service.serviceCount.current}
                            maximum={service.serviceCount.maximum}
                            onChange={(value) => handleCountChange(service.uuid, value)}
                          />
                        )}
                      </div>
                    </div>
                  </div>
                  {/* Price */}
                  <div className="text-size-body-sm flex-shrink-0 text-right whitespace-nowrap">
                    {service.included ? (
                      <div className="text-color-success font-semibold">
                        <FormattedMessage
                          key="Aplus.included-text"
                          id="Aplus.included"
                          defaultMessage="inklusive"
                        />
                      </div>
                    ) : (
                      <div>
                        {service.price?.value === 0 ? (
                          <div className="text-color-success font-semibold">
                            <FormattedMessage
                              key="Aplus.free-text"
                              id="Aplus.free"
                              defaultMessage="gratis"
                            />
                          </div>
                        ) : (
                          <Price size="small">
                            <PriceValue>
                              + {formatPrice(service.price?.value || 0, service.price?.currency)}
                            </PriceValue>
                          </Price>
                        )}
                      </div>
                    )}
                  </div>
                  {/* Info Icon */}
                  <div
                    className="icon-emp-information-circle-filled flex-shrink-0 cursor-pointer text-gray-500"
                    data-testid="info-icon"
                    aria-label={formatMessage(messages.aplusInfoAriaLabel)}
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      openServiceInfo(service.name!, service.uuid);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        openServiceInfo(service.name!, service.uuid);
                      }
                    }}
                  />
                </div>
              ))}
            </div>
          ))}
          {activeServiceDialog && (
            <Dialog isOpen onClose={() => setActiveServiceDialog(undefined)}>
              <DialogHeader onClose={() => setActiveServiceDialog(undefined)}>
                {activeServiceDialog.name}
              </DialogHeader>
              <DialogContent>
                <ServiceLayer serviceLayerId={`a-plus_${activeServiceDialog.uuid}`} />
              </DialogContent>
            </Dialog>
          )}
        </AccordionDetails>
      </AccordionItem>
    </Accordion>
  );
};
